import { Link } from 'react-router-dom';
import { Target, Eye, HeartHandshake, ArrowRight, MessageCircle } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionHeading from '../components/SectionHeading';
import { FadeUp } from '../components/Reveal';
import Marquee from '../components/Marquee';
import { about, images, normsMarquee, site, waUrl, DEFAULT_WA_MESSAGE } from '../data/site';

const PILLAR_ICONS = { Missão: Target, Visão: Eye, Valores: HeartHandshake };

export default function QuemSomos() {
  return (
    <>
      <PageHeader
        index="01"
        eyebrow="Institucional"
        title={about.title}
        description="Técnica, arte e funcionalidade: uma atuação multidisciplinar que integra engenharia civil, arquitetura e design de interiores — da fundação ao acabamento, do conceito ao detalhe."
        image={images.facade}
      />
      <Marquee items={normsMarquee} />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="institutional-narrative">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Quem Somos" title="Muito além da reforma: engenharia com olhar técnico-artístico" />
            <FadeUp delay={0.15}>
              <p className="mt-8 leading-relaxed text-muted-c md:text-lg">{about.description}</p>
            </FadeUp>
            <FadeUp delay={0.25}>
              <p className="mt-6 leading-relaxed text-muted-c">{about.narrative}</p>
            </FadeUp>
            <FadeUp delay={0.3} className="mt-8">
              <p className="font-display text-lg font-bold leading-snug text-navy dark:text-gold-light">
                “{site_motto()}”
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-5">
            <div className="relative grid place-items-center rounded-3xl border border-line bg-[var(--surface)] p-10 shadow-[0_40px_90px_-45px_rgba(13,33,69,0.45)] md:p-14">
              <span className="absolute -left-3 -top-3 select-none font-display text-2xl text-gold" aria-hidden="true">+</span>
              <span className="absolute -bottom-3 -right-3 select-none font-display text-2xl text-gold" aria-hidden="true">+</span>
              <img
                src={site.logo}
                alt="Logotipo Grupo Majo — Engenharia, Processos e Intermediações"
                className="w-full max-w-sm rounded-2xl bg-white p-4"
                data-testid="quem-somos-logo"
              />
              <p className="mt-7 text-center font-display text-[11px] font-bold uppercase tracking-[0.3em] text-muted-c">
                Desde 2010 · Engenharia, Processos & Intermediações
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-y border-line bg-[var(--surface)]" data-testid="pillars-section">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Pilares do Grupo"
            title="Missão, visão e valores que sustentam cada obra"
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {about.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[p.name];
              return (
                <FadeUp key={p.name} delay={i * 0.1}>
                  <article className="card-hover h-full rounded-2xl surface p-8" data-testid={`pillar-card-${i}`}>
                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-gold-light dark:bg-white/10">
                      <Icon size={22} strokeWidth={1.8} />
                    </div>
                    <h3 className="mt-5 font-display text-xl font-bold text-navy dark:text-white">{p.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-c">{p.text}</p>
                  </article>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="values-section">
        <SectionHeading
          eyebrow="Nossos Valores"
          title="Sete compromissos que atravessam cada parede que levantamos"
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {about.values.map((v, i) => (
            <FadeUp key={v.name} delay={(i % 4) * 0.07}>
              <article className="card-hover flex h-full flex-col rounded-2xl surface p-6" data-testid={`value-card-${i}`}>
                <span className="font-display text-3xl font-extrabold outline-numeral" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-4 font-display text-base font-bold leading-snug text-navy dark:text-white">{v.name}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-c">{v.text}</p>
              </article>
            </FadeUp>
          ))}
          <FadeUp delay={0.3}>
            <Link
              to="/simulador"
              data-testid="values-simulator-cta"
              className="group flex h-full min-h-[180px] flex-col justify-between rounded-2xl bg-navy p-6 text-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-24px_rgba(13,33,69,0.65)]"
            >
              <span className="font-display text-lg font-bold leading-snug">Pronto para começar com a gente?</span>
              <span className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-gold-light">
                Simular orçamento <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </FadeUp>
        </div>
      </section>

      <section className="border-y border-line bg-[var(--surface)]" data-testid="timeline-section">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Nossa História"
            title="Uma trajetória construída sobre rigor técnico"
          />
          <div className="relative mt-16 grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              {about.timeline.map((t, i) => (
                <FadeUp key={t.year} delay={i * 0.08}>
                  <div className="relative flex gap-6 pb-10 last:pb-0" data-testid={`timeline-item-${i}`}>
                    <div className="flex flex-col items-center">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-gold/50 bg-gold/10 font-display text-[11px] font-extrabold text-navy dark:text-gold-light">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {i < about.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-[var(--line)]" aria-hidden="true" />}
                    </div>
                    <div className="pb-2">
                      <p className="font-display text-sm font-extrabold uppercase tracking-[0.18em] text-azure dark:text-gold-light">{t.year}</p>
                      <p className="mt-2 leading-relaxed text-muted-c">{t.milestone}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.15}>
              <div className="relative h-full min-h-[320px] overflow-hidden rounded-3xl border border-line">
                <img src={images.family} alt="Família feliz recebendo as chaves do novo imóvel" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" aria-hidden="true" />
                <p className="absolute bottom-7 left-7 right-7 font-display text-xl font-bold leading-snug text-white">
                  “O sonho é seu, mas o trabalho é nosso.”
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-24 text-center lg:px-8">
        <FadeUp>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy dark:text-white md:text-4xl">
            Vamos construir o seu próximo capítulo?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-c">
            Conheça nosso responsável técnico ou vá direto ao simulador de orçamento.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3.5 sm:flex-row">
            <Link to="/responsavel-tecnico" data-testid="quem-somos-cta-engineer" className="btn-outline text-navy dark:text-white">
              Responsável Técnico
            </Link>
            <Link to="/simulador" data-testid="quem-somos-cta-simulator" className="btn-gold">
              Simular Orçamento <ArrowRight size={16} />
            </Link>
          </div>
          <a
            href={waUrl(DEFAULT_WA_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="quem-somos-cta-whatsapp"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-azure hover:underline dark:text-gold-light"
          >
            <MessageCircle size={15} /> Ou fale direto com o engenheiro
          </a>
        </FadeUp>
      </section>
    </>
  );
}

function site_motto() {
  return 'Transforme seus projetos em realidade — com segurança, conforto e beleza.';
}
